import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Interactive before/after image comparison slider.
 * Same source image is used for both sides; the "after" side has a darker /
 * cooler filter applied to simulate freshly tinted glass.
 *
 * Drag the handle (mouse / touch) or click anywhere on the image to move the
 * reveal line. Keyboard arrows move the handle when focused.
 */
export default function BeforeAfterSlider({ image, alt = "tint transformation", testId }) {
    const [pos, setPos] = useState(50);          // divider position, percent from left
    const [smooth, setSmooth] = useState(false); // animate transitions during the auto-nudge only
    const dragging = useRef(false);
    const nudged = useRef(false);
    const ref = useRef(null);

    const moveTo = useCallback((clientX) => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const p = ((clientX - rect.left) / rect.width) * 100;
        setPos(Math.max(2, Math.min(98, p)));
    }, []);

    useEffect(() => {
        const onMove = (e) => { if (dragging.current) moveTo(e.touches ? e.touches[0].clientX : e.clientX); };
        const onUp = () => { dragging.current = false; };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("touchmove", onMove, { passive: true });
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchend", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchend", onUp);
        };
    }, [moveTo]);

    const start = (e) => {
        dragging.current = true;
        setSmooth(false);                 // dragging must track the cursor instantly
        moveTo(e.touches ? e.touches[0].clientX : e.clientX);
    };

    // One-time "auto-nudge" when the slider scrolls into view to signal it's interactive.
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return; // no auto-nudge animation
        const io = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !nudged.current && !dragging.current) {
                nudged.current = true;
                setSmooth(true);
                setPos(34);
                setTimeout(() => { if (!dragging.current) setPos(66); }, 700);
                setTimeout(() => { if (!dragging.current) setPos(50); }, 1500);
                setTimeout(() => setSmooth(false), 2300);
            }
        }, { threshold: 0.45 });
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const onKey = (e) => {
        if (e.key === "ArrowLeft" || e.key === "ArrowDown") { e.preventDefault(); setPos((p) => Math.max(2, p - 4)); }
        if (e.key === "ArrowRight" || e.key === "ArrowUp") { e.preventDefault(); setPos((p) => Math.min(98, p + 4)); }
        if (e.key === "Home") { e.preventDefault(); setPos(2); }
        if (e.key === "End") { e.preventDefault(); setPos(98); }
    };

    return (
        <div
            ref={ref}
            data-testid={testId}
            className="relative aspect-[16/10] overflow-hidden bg-[#E2D4C1] select-none cursor-ew-resize"
            onMouseDown={start}
            onTouchStart={start}
        >
            {/* AFTER layer — darker, cooler, more contrast (simulates tinted glass) */}
            <img
                src={image}
                alt={`${alt} after`}
                draggable={false}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: "brightness(0.62) saturate(1.08) contrast(1.18) hue-rotate(-6deg)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-black/20" />

            {/* BEFORE layer — original, clipped to the LEFT of the slider */}
            <div
                className="absolute inset-y-0 left-0 overflow-hidden"
                style={{ width: `${pos}%`, transition: smooth ? "width 0.7s cubic-bezier(0.22,1,0.36,1)" : "none" }}
                aria-hidden="true"
            >
                <img
                    src={image}
                    alt={`${alt} before`}
                    draggable={false}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-y-0 left-0 h-full object-cover"
                    style={{ width: `${(100 / pos) * 100}%`, maxWidth: "none", transition: smooth ? "width 0.7s cubic-bezier(0.22,1,0.36,1)" : "none" }}
                />
            </div>

            {/* Labels */}
            <span className="absolute top-4 left-4 text-[10px] tracking-[0.35em] uppercase text-white bg-[#1A1A1A]/75 backdrop-blur px-3 py-1.5 border border-white/10">
                Before
            </span>
            <span className="absolute top-4 right-4 text-[10px] tracking-[0.35em] uppercase text-white bg-gold px-3 py-1.5">
                After
            </span>

            {/* Divider line + draggable handle */}
            <div
                className="absolute top-0 bottom-0 w-px bg-[#F7F3EB] shadow-[0_0_14px_rgba(140,90,60,0.55)] pointer-events-none"
                style={{ left: `${pos}%`, transition: smooth ? "left 0.7s cubic-bezier(0.22,1,0.36,1)" : "none" }}
            />
            <button
                type="button"
                role="slider"
                aria-label={`${alt}: compare before and after tint`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(pos)}
                aria-valuetext={`${Math.round(pos)}% before, ${100 - Math.round(pos)}% after`}
                tabIndex={0}
                onKeyDown={onKey}
                onMouseDown={(e) => { e.stopPropagation(); start(e); }}
                onTouchStart={(e) => { e.stopPropagation(); start(e); }}
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#F7F3EB] border border-gold shadow-md grid place-items-center text-gold hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-gold/60"
                style={{ left: `${pos}%`, transition: smooth ? "left 0.7s cubic-bezier(0.22,1,0.36,1)" : "transform 0.2s ease" }}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <polyline points="9 6 3 12 9 18" />
                    <polyline points="15 6 21 12 15 18" />
                </svg>
            </button>
        </div>
    );
}
